import { useCallback, useRef, useState } from 'react';
import { Editor } from '@tiptap/react';
import { useWorkbookImageUpload } from '@/hooks/content-generator/use-workbook-images';
import { toast } from '@/hooks/errors/use-toast';

// Development debug helpers
const DEBUG = process.env.NODE_ENV === 'development';

interface UseDirectImagePasteProps {
  editor: Editor | null;
  workbookId?: string;
  pageId?: string;
  enabled?: boolean;
}

interface UseDirectImagePasteReturn {
  detectAndHandleImagePaste: (event: ClipboardEvent) => boolean;
  handleDrop: (event: DragEvent, dropPosition?: number) => boolean;
  handlePasteImages: (imageFiles: File[]) => boolean;
  isUploading: boolean;
  uploadProgress: number;
}

/**
 * Hook for direct image paste functionality in TipTap Editor
 * Provides synchronous detection for TipTap's handlePaste and async upload with insertion
 */
export function useDirectImagePaste({
  editor,
  workbookId,
  pageId,
  enabled = true
}: UseDirectImagePasteProps): UseDirectImagePasteReturn {
  const uploadMutation = useWorkbookImageUpload();
  const cursorPositionRef = useRef<{ from: number; to: number } | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  /**
   * Synchronous detection function for TipTap's handlePaste
   * Returns true if we should handle this paste (file-based images), false otherwise
   */
  const detectAndHandleImagePaste = useCallback((event: ClipboardEvent): boolean => {
    // Early returns if not enabled or missing required data
    if (!enabled || !editor || !event.clipboardData?.items) {
      return false;
    }

    const items = Array.from(event.clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));

    if (imageItems.length === 0) {
      if (DEBUG) console.log('[useDirectImagePaste] No images found in clipboard');
      return false; // Let TipTap handle normal text/other content
    }

    // Check if any image items are file-based (local data) vs URL-based (web images)
    const hasFileBasedImages = imageItems.some(item => item.kind === 'file');

    if (!hasFileBasedImages) {
      if (DEBUG) console.log('[useDirectImagePaste] Only URL-based images found, letting TipTap handle');
      return false; // Let TipTap handle web images normally
    }

    if (DEBUG) console.log('[useDirectImagePaste] File-based images detected, handling upload');

    // Save cursor position for insertion after upload
    const { from, to } = editor.state.selection;
    cursorPositionRef.current = { from, to };

    // Start async upload process (don't await - this must be synchronous)
    handleAsyncImageUpload(imageItems);

    return true; // Prevent TipTap's default paste behavior
  }, [enabled, editor, workbookId, pageId]);

  /**
   * Drag-and-drop handler for images with drop position
   * Returns true if we handled the drop (file-based images), false otherwise
   */
  const handleDrop = useCallback((event: DragEvent, dropPosition?: number): boolean => {
    // Early returns if not enabled or missing required data
    if (!enabled || !editor || !event.dataTransfer?.files) {
      return false;
    }

    const files = Array.from(event.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      if (DEBUG) console.log('[useDirectImagePaste] No image files found in drop');
      return false; // Let TipTap handle other content
    }

    if (DEBUG) console.log('[useDirectImagePaste] Image files detected in drop, handling upload');

    // Use drop position if provided, otherwise fallback to cursor position
    if (dropPosition !== undefined) {
      if (DEBUG) console.log('[useDirectImagePaste] Using drop position:', dropPosition);
      cursorPositionRef.current = { from: dropPosition, to: dropPosition };
    } else {
      if (DEBUG) console.log('[useDirectImagePaste] Drop position not available, using cursor position as fallback');
      const { from, to } = editor.state.selection;
      cursorPositionRef.current = { from, to };
    }

    // Start async upload process with files directly (don't await - this must be synchronous)
    handleAsyncImageUploadFromFiles(imageFiles);

    return true; // Prevent TipTap's default drop behavior
  }, [enabled, editor, workbookId, pageId]);

  /**
   * Paste handler for images - always uses cursor position
   */
  const handlePasteImages = useCallback((imageFiles: File[]) => {
    if (!enabled || !editor) {
      if (DEBUG) console.log('[useDirectImagePaste] Paste handler: missing requirements');
      return false;
    }

    if (DEBUG) console.log('[useDirectImagePaste] Paste images detected, using cursor position');

    // Always use cursor position for paste
    const { from, to } = editor.state.selection;
    cursorPositionRef.current = { from, to };

    // Start async upload process
    handleAsyncImageUploadFromFiles(imageFiles);
    return true;
  }, [enabled, editor]);

  /**
   * Async upload handler for dropped files - processes File objects directly
   * Enhanced to support batch uploads with progress tracking
   */
  const handleAsyncImageUploadFromFiles = useCallback(async (imageFiles: File[]) => {
    if (!editor) {
      return;
    }

    const totalFiles = imageFiles.length;
    const maxFiles = 3; // Limit to 3 images at once for performance

    // Limit files if too many
    const filesToProcess = imageFiles.slice(0, maxFiles);
    
    if (totalFiles > maxFiles) {
      toast({
        title: `Processing first ${maxFiles} images`,
        description: `You dropped ${totalFiles} images. Processing the first ${maxFiles} for performance.`,
        variant: "default",
      });
    }

    try {
      // Process multiple image files
      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        if (DEBUG) {
          console.log('[useDirectImagePaste] Processing dropped file:', {
            name: file.name || 'dropped-image',
            type: file.type,
            size: file.size
          });
        }

        // Create a proper file with a name if it doesn't have one
        const namedFile = file.name 
          ? file 
          : new File([file], `dropped-image-${Date.now()}.${file.type.split('/')[1]}`, { type: file.type });

        // ✅ UPFRONT VALIDATION - Same as modal behavior
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        const maxSizeMB = 10; // 10MB - WebP conversion handles optimization

        // File type validation
        if (!allowedTypes.includes(namedFile.type)) {
          const allowedExtensions = allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ');
          toast({
            title: "Invalid file type",
            description: `Please drop ${allowedExtensions} images only.`,
            variant: "destructive",
          });
          return; // Stop processing
        }

        // File size validation
        const sizeInMB = namedFile.size / (1024 * 1024);
        if (sizeInMB > maxSizeMB) {
          const maxSizeKB = Math.round(maxSizeMB * 1024);
          toast({
            title: "Image too large",
            description: `Maximum size allowed is ${maxSizeKB}KB. Your dropped image is ${sizeInMB.toFixed(2)}MB.`,
            variant: "destructive",
          });
          return; // Stop processing
        }

        // Show upload feedback with progress (batch-aware)
        const isMultiple = filesToProcess.length > 1;
        setUploadProgress(0);
        const uploadToast = toast({
          title: isMultiple ? `Uploading image ${i + 1} of ${filesToProcess.length}...` : "Uploading image...",
          description: isMultiple ? 
            `Processing ${file.name || `image-${i + 1}`}` : 
            "Your dropped image is being processed.",
          duration: 0, // Keep open until we dismiss it
        });

        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 15, 90));
        }, 80);

        try {
          // Upload the image
          const uploadedImage = await uploadMutation.mutateAsync({
            file: namedFile,
            workbookId,
            pageId,
            altText: 'Dropped image',
            isGenerated: false,
          });

          // Complete progress
          clearInterval(progressInterval);
          setUploadProgress(100);

          // Insert image into editor at saved cursor position
          insertImageIntoEditor(uploadedImage.image_url, uploadedImage.alt_text || 'Dropped image');
          
          // Dismiss upload toast
          uploadToast.dismiss?.();
          
          // Add small delay between batch uploads for better UX
          if (i < filesToProcess.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } catch (uploadError) {
          clearInterval(progressInterval);
          setUploadProgress(0);
          uploadToast.dismiss?.();
          
          // For batch uploads, show individual error but continue with other files
          if (filesToProcess.length > 1) {
            toast({
              title: `Failed to upload image ${i + 1}`,
              description: `Error uploading ${file.name || `image-${i + 1}`}. Continuing with remaining images.`,
              variant: "destructive",
            });
            continue; // Continue with next file in batch
          } else {
            throw uploadError; // Single file - throw to outer catch
          }
        }
      }
      
      // Show batch completion message
      if (filesToProcess.length > 1) {
        toast({
          title: "Batch upload completed",
          description: `Successfully processed ${filesToProcess.length} images.`,
          variant: "default",
        });
      }
    } catch (error) {
      if (DEBUG) console.error('[useDirectImagePaste] Drop upload error:', error);
      toast({
        title: "Drop failed",
        description: error instanceof Error ? error.message : "Failed to upload dropped image. Please try again.",
        variant: "destructive",
      });
    }
  }, [editor, workbookId, pageId, uploadMutation]);

  /**
   * Async upload handler - processes file-based images and inserts into editor
   */
  const handleAsyncImageUpload = useCallback(async (imageItems: DataTransferItem[]) => {
    if (!editor) return;

    try {
      // Process the first file-based image found
      for (const item of imageItems) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            if (DEBUG) {
              console.log('[useDirectImagePaste] Processing file:', {
                name: file.name || 'pasted-image',
                type: file.type,
                size: file.size
              });
            }

            // Create a proper file with a name if it doesn't have one
            const namedFile = file.name 
              ? file 
              : new File([file], `pasted-image-${Date.now()}.${file.type.split('/')[1]}`, { type: file.type });

            // ✅ UPFRONT VALIDATION - Same as modal behavior
            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
            const maxSizeMB = 10; // 10MB - WebP conversion handles optimization

            // File type validation
            if (!allowedTypes.includes(namedFile.type)) {
              const allowedExtensions = allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ');
              toast({
                title: "Invalid file type",
                description: `Please paste ${allowedExtensions} images only.`,
                variant: "destructive",
              });
              return; // Stop processing
            }

            // File size validation
            const sizeInMB = namedFile.size / (1024 * 1024);
            if (sizeInMB > maxSizeMB) {
              const maxSizeKB = Math.round(maxSizeMB * 1024);
              toast({
                title: "Image too large",
                description: `Maximum size allowed is ${maxSizeKB}KB. Your pasted image is ${sizeInMB.toFixed(2)}MB.`,
                variant: "destructive",
              });
              return; // Stop processing
            }

            // Show upload feedback with progress
            setUploadProgress(0);
            const uploadToast = toast({
              title: "Uploading image...",
              description: "Your pasted image is being processed.",
              duration: 0, // Keep open until we dismiss it
            });

            // Simulate progress for better UX
            const progressInterval = setInterval(() => {
              setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 100);

            try {
              // Upload the image
              const uploadedImage = await uploadMutation.mutateAsync({
                file: namedFile,
                workbookId,
                pageId,
                altText: 'Pasted image',
                isGenerated: false,
              });

              // Complete progress
              clearInterval(progressInterval);
              setUploadProgress(100);

              // Insert image into editor at saved cursor position
              insertImageIntoEditor(uploadedImage.image_url, uploadedImage.alt_text || 'Pasted image');
              
              // Dismiss upload toast
              uploadToast.dismiss?.();
              
              // Only process the first image to avoid multiple insertions
              break;
            } catch (uploadError) {
              clearInterval(progressInterval);
              setUploadProgress(0);
              uploadToast.dismiss?.();
              throw uploadError;
            }
          }
        }
      }
    } catch (error) {
      if (DEBUG) console.error('[useDirectImagePaste] Upload error:', error);
      toast({
        title: "Paste failed",
        description: error instanceof Error ? error.message : "Failed to paste image. Please try again.",
        variant: "destructive",
      });
    }
  }, [editor, workbookId, pageId, uploadMutation]);

  /**
   * Insert uploaded image into TipTap editor at the saved cursor position
   */
  const insertImageIntoEditor = useCallback((imageUrl: string, altText: string) => {
    if (!editor) return;

    try {
      // Restore cursor position if available
      if (cursorPositionRef.current) {
        const docSize = editor.state.doc.content.size;
        if (cursorPositionRef.current.from <= docSize && cursorPositionRef.current.to <= docSize) {
          editor.commands.setTextSelection(cursorPositionRef.current);
        } else {
          editor.commands.focus('end');
        }
      } else {
        editor.commands.focus();
      }

      // Check if ResizableImage extension is available and use it, otherwise fallback to standard Image
      const hasResizableImage = editor.extensionManager.extensions.some(ext => ext.name === 'resizableImage');
      
      if (hasResizableImage) {
        // Use ResizableImage extension command
        if (DEBUG) console.log('[useDirectImagePaste] Using ResizableImage extension for insertion');
        editor
          .chain()
          .focus()
          .setResizableImage({ 
            src: imageUrl,
            alt: altText
          })
          .run();
      } else {
        // Fallback to standard Image extension
        if (DEBUG) console.log('[useDirectImagePaste] Using standard Image extension for insertion');
        editor
          .chain()
          .focus()
          .setImage({ 
            src: imageUrl,
            alt: altText
          })
          .run();
      }

      if (DEBUG) console.log('[useDirectImagePaste] Image successfully inserted into editor');

      // Reset progress after successful insertion
      setTimeout(() => setUploadProgress(0), 1000);

      toast({
        title: "Image inserted successfully",
        description: "Your image has been uploaded and added to the document.",
        variant: "default",
      });

      // Clear saved cursor position
      cursorPositionRef.current = null;

    } catch (error) {
      if (DEBUG) console.error('[useDirectImagePaste] Error inserting image into editor:', error);
      toast({
        title: "Insert failed",
        description: "Image uploaded but failed to insert. Please try again.",
        variant: "destructive",
      });
    }
  }, [editor]);

  return {
    detectAndHandleImagePaste,
    handleDrop,
    handlePasteImages, // New: dedicated paste handler
    isUploading: uploadMutation.isPending,
    uploadProgress
  };
} 